/**
 * Robuste Utility-Funktion für Restaurant-Status (Open/Closed)
 * 
 * Features:
 * - Mehrere Intervalle pro Tag (z.B. 11-14, 17-22)
 * - Pausen berücksichtigen
 * - Über-Mitternacht-Zeiten (z.B. 22:00-00:30)
 * - Europe/Berlin Zeitzone
 * - Strukturiertes Output mit nextChangeLabel
 * - Unterstützt beide Wochentag-Formate (JS: 0-6 und ISO: 1-7)
 * 
 * @module utils/restaurantStatus
 */

import { OpeningHour } from '@/lib/types';

export interface OpenStatus {
  isOpen: boolean;
  closesAt?: Date;
  opensAt?: Date;
  minutesToClose?: number;
  minutesToOpen?: number;
  minutesSinceOpen?: number;
  nextChangeLabel: string;
  currentInterval?: {
    openStart: string;
    openEnd: string;
  };
}

// Wochentag-Namen für beide Formate
const WEEKDAY_NAMES_JS = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
const WEEKDAY_NAMES_ISO = ['', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

/**
 * Normalisiert Zeit-String (HH:MM:SS -> HH:MM)
 */
function normalizeTime(time: string | null | undefined): string {
  if (!time) return '';
  const timeStr = String(time);
  return timeStr.split(':').slice(0, 2).join(':');
}

/**
 * Konvertiert HH:MM String zu Minuten seit Mitternacht
 */
function timeToMinutes(timeStr: string): number {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return (hours || 0) * 60 + (minutes || 0);
}

// The following utility functions are preserved but commented out for potential future use:
// function minutesToTime(minutes: number): string {
//   const hours = Math.floor(minutes / 60) % 24;
//   const mins = minutes % 60;
//   return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
// }
// function toJsWeekday(day: number, format: 'js' | 'iso'): number {
//   if (format === 'js') return day;
//   return day === 7 ? 0 : day;
// }

/**
 * Erkennt, welches Wochentag-Format die Daten verwenden
 * JS-Format: 0=Sonntag, 1-6=Mon-Sat (Werte 0-6)
 * ISO-Format: 1-7=Mon-Sun (Werte 1-7)
 */
function detectWeekdayFormat(openingHours: OpeningHour[]): 'js' | 'iso' {
  for (const hour of openingHours) {
    const dayValue = hour.weekday !== undefined ? hour.weekday : hour.day;
    if (dayValue === 0) return 'js';  // 0 existiert nur in JS-Format
    if (dayValue === 7) return 'iso'; // 7 existiert nur in ISO-Format
  }
  // Default: JS-Format (Speiseguru DB Schema)
  return 'js';
}

/**
 * Gibt den Wochentag-Namen zurück
 */
function getWeekdayName(day: number, format: 'js' | 'iso'): string {
  if (format === 'js') {
    return WEEKDAY_NAMES_JS[day] || `Tag ${day}`;
  }
  return WEEKDAY_NAMES_ISO[day] || `Tag ${day}`;
}

/**
 * Prüft, ob eine Zeit innerhalb eines Intervalls liegt (mit Pause)
 */
function isTimeInInterval(
  currentMinutes: number,
  openStart: string,
  openEnd: string,
  pauseStart?: string | null,
  pauseEnd?: string | null
): boolean {
  const startMinutes = timeToMinutes(openStart);
  let endMinutes = timeToMinutes(openEnd);
  
  // Über-Mitternacht: Wenn closeTime < openTime
  if (endMinutes <= startMinutes) {
    endMinutes += 24 * 60;
  }
  
  // Prüfe ob innerhalb des Hauptintervalls
  let adjustedCurrent = currentMinutes;
  if (currentMinutes < startMinutes && endMinutes > 24 * 60) {
    // Wir sind nach Mitternacht, das Intervall ging über Mitternacht
    adjustedCurrent = currentMinutes + 24 * 60;
  }
  
  const inInterval = adjustedCurrent >= startMinutes && adjustedCurrent < endMinutes;
  if (!inInterval) return false;
  
  // Prüfe Pause
  if (pauseStart && pauseEnd) {
    const pauseStartMinutes = timeToMinutes(pauseStart);
    let pauseEndMinutes = timeToMinutes(pauseEnd);
    
    if (pauseEndMinutes <= pauseStartMinutes) {
      pauseEndMinutes += 24 * 60;
    }
    
    if (adjustedCurrent >= pauseStartMinutes && adjustedCurrent < pauseEndMinutes) {
      return false;
    }
  }
  
  return true;
}

/**
 * Ermittelt die aktuelle Zeit in Europe/Berlin Zeitzone
 */
function getCurrentTimeInBerlin(): { date: Date; jsDay: number; minutes: number } {
  const now = new Date();
  const berlinTime = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).formatToParts(now);
  
  const hour = parseInt(berlinTime.find(p => p.type === 'hour')?.value || '0');
  const minute = parseInt(berlinTime.find(p => p.type === 'minute')?.value || '0');
  const dayNum = parseInt(berlinTime.find(p => p.type === 'day')?.value || '1');
  const month = parseInt(berlinTime.find(p => p.type === 'month')?.value || '1');
  const year = parseInt(berlinTime.find(p => p.type === 'year')?.value || '2024');
  
  const berlinDate = new Date(year, month - 1, dayNum, hour, minute);
  const jsDay = berlinDate.getDay(); // 0=Sonntag, 1-6=Mon-Sat (JS native)
  const currentMinutes = hour * 60 + minute;
  
  return { date: berlinDate, jsDay, minutes: currentMinutes };
}

/**
 * Hauptfunktion: Ermittelt den aktuellen Open/Closed Status
 */
export function getOpenStatus(
  openingHours: OpeningHour[] | null | undefined,
  _timezone: string = 'Europe/Berlin'
): OpenStatus {
  const defaultStatus: OpenStatus = {
    isOpen: false,
    nextChangeLabel: 'Öffnungszeiten nicht verfügbar'
  };
  
  if (!openingHours || !Array.isArray(openingHours) || openingHours.length === 0) {
    return defaultStatus;
  }
  
  const { date: now, jsDay: currentJsDay, minutes: currentMinutes } = getCurrentTimeInBerlin();
  const format = detectWeekdayFormat(openingHours);
  
  // Konvertiere aktuellen Tag zum Format der Daten
  const currentDay = format === 'js' 
    ? currentJsDay 
    : (currentJsDay === 0 ? 7 : currentJsDay);
  
  // Finde alle Öffnungszeiten für heute
  const todayHours = openingHours
    .filter(hour => {
      const dayValue = hour.weekday !== undefined ? hour.weekday : hour.day;
      return dayValue === currentDay;
    })
    .map(hour => ({
      openStart: normalizeTime(hour.openStart || hour.opening || ''),
      openEnd: normalizeTime(hour.openEnd || hour.closing || ''),
      pauseStart: hour.pauseStart ? normalizeTime(hour.pauseStart) : null,
      pauseEnd: hour.pauseEnd ? normalizeTime(hour.pauseEnd) : null
    }))
    .filter(h => h.openStart && h.openEnd)
    .sort((a, b) => a.openStart.localeCompare(b.openStart));
  
  // Prüfe auch gestern (für Über-Mitternacht)
  const yesterdayDay = format === 'js'
    ? (currentJsDay === 0 ? 6 : currentJsDay - 1)
    : (currentDay === 1 ? 7 : currentDay - 1);
  
  const yesterdayHours = openingHours
    .filter(hour => {
      const dayValue = hour.weekday !== undefined ? hour.weekday : hour.day;
      return dayValue === yesterdayDay;
    })
    .map(hour => ({
      openStart: normalizeTime(hour.openStart || hour.opening || ''),
      openEnd: normalizeTime(hour.openEnd || hour.closing || ''),
      pauseStart: hour.pauseStart ? normalizeTime(hour.pauseStart) : null,
      pauseEnd: hour.pauseEnd ? normalizeTime(hour.pauseEnd) : null
    }))
    .filter(h => h.openStart && h.openEnd);
  
  // Prüfe zuerst gestern (für Über-Mitternacht-Zeiten)
  for (const hour of yesterdayHours) {
    const endMinutes = timeToMinutes(hour.openEnd);
    const startMinutes = timeToMinutes(hour.openStart);
    
    // Nur prüfen wenn es über Mitternacht geht
    if (endMinutes <= startMinutes && currentMinutes < endMinutes) {
      // Aktuell noch im gestrigen Intervall (über Mitternacht)
      const closesAt = new Date(now);
      closesAt.setHours(Math.floor(endMinutes / 60), endMinutes % 60, 0, 0);
      
      const minutesToClose = endMinutes - currentMinutes;
      
      return {
        isOpen: true,
        closesAt,
        minutesToClose,
        nextChangeLabel: minutesToClose <= 30 
          ? `Schließt in ${minutesToClose} Min`
          : `Heute geöffnet bis ${hour.openEnd} Uhr`,
        currentInterval: { openStart: hour.openStart, openEnd: hour.openEnd }
      };
    }
  }
  
  // Prüfe heutige Intervalle
  for (const hour of todayHours) {
    if (isTimeInInterval(currentMinutes, hour.openStart, hour.openEnd, hour.pauseStart, hour.pauseEnd)) {
      // Aktuell geöffnet!
      let endMinutes = timeToMinutes(hour.openEnd);
      const startMinutes = timeToMinutes(hour.openStart);
      
      if (endMinutes <= startMinutes) {
        endMinutes += 24 * 60;
      }
      
      let minutesToClose = endMinutes - currentMinutes;
      if (currentMinutes < startMinutes) {
        // Wir sind nach Mitternacht, Anpassung nötig
        minutesToClose = endMinutes - (currentMinutes + 24 * 60);
      }
      
      const closesAt = new Date(now);
      const closeHour = endMinutes >= 24 * 60 ? endMinutes - 24 * 60 : endMinutes;
      closesAt.setHours(Math.floor(closeHour / 60) % 24, closeHour % 60, 0, 0);
      if (endMinutes >= 24 * 60) {
        closesAt.setDate(closesAt.getDate() + 1);
      }
      
      const minutesSinceOpen = currentMinutes - startMinutes;
      
      return {
        isOpen: true,
        closesAt,
        minutesToClose: Math.max(0, minutesToClose),
        minutesSinceOpen: (minutesSinceOpen > 0 && minutesSinceOpen <= 10) ? minutesSinceOpen : undefined,
        nextChangeLabel: minutesToClose <= 30 
          ? `Schließt in ${minutesToClose} Min`
          : `Heute geöffnet bis ${hour.openEnd} Uhr`,
        currentInterval: { openStart: hour.openStart, openEnd: hour.openEnd }
      };
    }
  }
  
  // Aktuell geschlossen - prüfe ob später heute noch geöffnet wird
  for (const hour of todayHours) {
    const startMinutes = timeToMinutes(hour.openStart);
    if (currentMinutes < startMinutes) {
      const minutesToOpen = startMinutes - currentMinutes;
      const opensAt = new Date(now);
      opensAt.setHours(Math.floor(startMinutes / 60), startMinutes % 60, 0, 0);
      
      return {
        isOpen: false,
        opensAt,
        minutesToOpen,
        nextChangeLabel: minutesToOpen <= 60 
          ? `Öffnet in ${minutesToOpen} Min`
          : `Öffnet um ${hour.openStart} Uhr`
      };
    }
  }
  
  // Heute geschlossen - finde nächste Öffnung
  return getNextOpeningStatus(openingHours, currentDay, format, now);
}

/**
 * Findet die nächste Öffnungszeit (wenn heute geschlossen)
 */
function getNextOpeningStatus(
  openingHours: OpeningHour[],
  currentDay: number,
  format: 'js' | 'iso',
  now: Date
): OpenStatus {
  // Note: maxDay/minDay could be used for validation but are currently not needed
  // const maxDay = format === 'js' ? 6 : 7;
  // const minDay = format === 'js' ? 0 : 1;
  
  for (let dayOffset = 1; dayOffset <= 7; dayOffset++) {
    let targetDay: number;
    if (format === 'js') {
      targetDay = (currentDay + dayOffset) % 7;
    } else {
      targetDay = ((currentDay - 1 + dayOffset) % 7) + 1;
    }
    
    const dayHours = openingHours
      .filter(hour => {
        const dayValue = hour.weekday !== undefined ? hour.weekday : hour.day;
        return dayValue === targetDay;
      })
      .map(hour => ({
        openStart: normalizeTime(hour.openStart || hour.opening || ''),
        openEnd: normalizeTime(hour.openEnd || hour.closing || '')
      }))
      .filter(h => h.openStart && h.openEnd)
      .sort((a, b) => a.openStart.localeCompare(b.openStart));
    
    if (dayHours.length > 0) {
      const firstOpening = dayHours[0].openStart;
      const opensAt = new Date(now);
      opensAt.setDate(opensAt.getDate() + dayOffset);
      const [hours, minutes] = firstOpening.split(':').map(Number);
      opensAt.setHours(hours, minutes, 0, 0);
      
      const minutesToOpen = Math.max(0, Math.floor((opensAt.getTime() - now.getTime()) / (1000 * 60)));
      
      const weekdayName = getWeekdayName(targetDay, format);
      
      let nextChangeLabel: string;
      if (dayOffset === 1) {
        nextChangeLabel = `Morgen ab ${firstOpening} Uhr`;
      } else {
        nextChangeLabel = `${weekdayName} ab ${firstOpening} Uhr`;
      }
      
      return {
        isOpen: false,
        opensAt,
        minutesToOpen,
        nextChangeLabel
      };
    }
  }
  
  return {
    isOpen: false,
    nextChangeLabel: 'Öffnungszeiten nicht verfügbar'
  };
}

/**
 * Lieferstatus (analog zu getOpenStatus, aber für deliveryHours)
 */
export function getDeliveryStatus(
  deliveryHours: OpeningHour[] | null | undefined,
  cutoffBufferMinutes: number = 30,
  timezone: string = 'Europe/Berlin'
): OpenStatus & { isOrderCutoffReached?: boolean; orderCutoffTime?: Date } {
  const baseStatus = getOpenStatus(deliveryHours, timezone);
  
  if (!baseStatus.isOpen || !baseStatus.closesAt) {
    return { ...baseStatus, isOrderCutoffReached: false };
  }
  
  const orderCutoffTime = new Date(baseStatus.closesAt);
  orderCutoffTime.setMinutes(orderCutoffTime.getMinutes() - cutoffBufferMinutes);
  
  const now = new Date();
  const isOrderCutoffReached = now >= orderCutoffTime;
  
  return {
    ...baseStatus,
    isOrderCutoffReached,
    orderCutoffTime
  };
}

